import { type CountryCode, type ProjectCode } from '@constants'
import { type ValueOf } from '@dni-types/utilities/value-of'
import { type CampaignBanner } from './campaigns'

export interface CrossBrandMallListItem {
  id: number
  /** 後端會為每個跨品牌賣場暫時寫死一個唯一的代碼 */
  code: string
  name: string
  country_code: CountryCode
  is_active: boolean
  currency: string
  start_at?: string | null
  end_at?: string | null
  campaign_count?: number
  created_at?: string
  updated_at?: string
}

export interface CrossBrandMallAdminBanner {
  pc_image_id: number | null
  pc_image_url: string | null
  mobile_image_id: number | null
  mobile_image_url: string | null
  link_url: string | null
}

export interface CrossBrandMallAdminBannerGroup {
  major_images?: CrossBrandMallAdminBanner[]
  /** STM 業務上不使用 minor_images，僅為對齊後端共用 banner 模組保留 */
  minor_images?: CrossBrandMallAdminBanner[] | null
}

export interface CrossBrandMallAdminBannerGroupPayload {
  major_images: CrossBrandMallAdminBanner[]
  minor_images: []
}

export interface CrossBrandMallCampaignPayload {
  campaign_id: number
  display_order: number
}

export interface CrossBrandMallDetailCampaign {
  id: number
  code: string
  name: string
  display_order: number
}

export interface CrossBrandMallCampaign {
  id: number
  code: string
  project_code: ProjectCode
}

export interface CrossBrandMallDetail extends CrossBrandMallListItem {
  updated_by: number
  campaigns: CrossBrandMallDetailCampaign[]
  /**
   * 後端在尚未設定 banner 時會回空陣列 `[]`（共用 banner 模組行為）
   * 已設定時回 `{ major_images }` 物件
   */
  banner: CrossBrandMallAdminBannerGroup | []
  timezone: string
}

// ─── 前台首頁 API types ───────────────────────────────────────────────────────

export interface CrossBrandMallHighlightCoupon {
  coupon_id: number
  display_order: number
}

export const CrossBrandMallNewArrivalImageDeviceType = {
  PC: 'pc',
  MOBILE: 'mobile',
} as const

export type CrossBrandMallNewArrivalImageDeviceType = ValueOf<typeof CrossBrandMallNewArrivalImageDeviceType>


export interface CrossBrandMallNewArrivalImage {
  image_id: number
  url: string
  link_url: string | null
  device_type: CrossBrandMallNewArrivalImageDeviceType
}

export interface CrossBrandMallLogoImage {
  image_id: number
  url: string
}

export interface CrossBrandMallTopSellerProduct {
  rank: number
  campaign_product: {
    campaign_id: number
    product_id: string
    parent_product_id: string
    project_code: ProjectCode
    ext_site_id: number
    sku: string
    display_name: string
    shipping_name: string
    title: string
    thumbnail: string
  }
}

export interface CrossBrandMallHomeCampaign {
  campaign_id: number
  code: string
  name: string
  project_code: ProjectCode
  display_order: number
  /** 空陣列 = 使用前端預設品牌圖，見 brandImageMap */
  logos: CrossBrandMallLogoImage[]
  new_arrival_image: CrossBrandMallNewArrivalImage[]
  highlight_coupons: CrossBrandMallHighlightCoupon[]
  top_sellers: CrossBrandMallTopSellerProduct[]
}

export interface CrossBrandMallHomeDetail {
  id: number
  name: string
  country_code: CountryCode
  banner: CampaignBanner[]
  campaigns: CrossBrandMallHomeCampaign[]
}

export interface CampaignTopSellerSuggestion {
  total_revenue: number
  campaign_product: {
    campaign_id: number
    product_id: string
    parent_product_id: string | null
    project_code: string | null
    ext_site_id: number | null
    sku: string | null
    display_name: string | null
    shipping_name: string | null
    title: string | null
    thumbnail: string | null
  }
}
